import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { verifyCertificate } from "@/lib/journey.functions";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Award, CheckCircle2, XCircle } from "lucide-react";

export const Route = createFileRoute("/verify/$code")({
  head: ({ params }) => ({
    meta: [
      { title: `Verify Certificate · KAfrequency` },
      { name: "description", content: `Public verification page for KAfrequency certificate ${params.code}.` },
      { name: "robots", content: "index,follow" },
    ],
  }),
  component: VerifyPage,
});

function VerifyPage() {
  const { code } = Route.useParams();
  const load = useServerFn(verifyCertificate);
  const [row, setRow] = useState<Awaited<ReturnType<typeof verifyCertificate>> | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "missing">("loading");
  useEffect(() => {
    load({ data: { code } })
      .then((r) => { setRow(r); setState(r ? "ok" : "missing"); })
      .catch(() => setState("missing"));
  }, [code, load]);
  return (
    <div className="min-h-dvh bg-obsidian text-foreground">
      <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6">
        <Link to="/" className="text-sm text-gold underline underline-offset-4">← KAfrequency</Link>
        <Card className="mt-6 p-8 text-center">
          {state === "loading" && <p className="text-foreground/60">Verifying…</p>}
          {state === "missing" && (
            <>
              <XCircle className="mx-auto text-foreground/50" size={48} />
              <h1 className="mt-3 font-serif text-2xl text-gold-luminous">Certificate not found</h1>
              <p className="mt-2 text-sm text-foreground/60">This verification code does not match any issued certificate.</p>
            </>
          )}
          {state === "ok" && row && (
            <>
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 text-gold">
                <Award size={28} />
              </div>
              <h1 className="mt-4 font-serif text-3xl text-gold-luminous">{row.program_title}</h1>
              <p className="mt-1 text-xs uppercase tracking-widest text-foreground/50">Verified KAfrequency Certificate</p>
              <div className="mt-6 space-y-2 text-sm">
                <div><span className="text-foreground/60">Awarded to:</span> <span className="font-medium">{row.recipient_name}</span></div>
                <div><span className="text-foreground/60">Program type:</span> {row.program_kind}</div>
                <div><span className="text-foreground/60">Issued:</span> {new Date(row.issued_on).toLocaleDateString()}</div>
                <div className="text-xs text-foreground/50">Code: <span className="font-mono">{row.verify_code}</span></div>
              </div>
              <div className="mt-6 inline-flex items-center gap-2 text-emerald-400">
                <CheckCircle2 size={16} /><span className="text-sm">Authentic</span>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
